import React, { useState, useEffect } from "react";
import validator from "validator";
import { useForm } from "react-hook-form";
import axios from "axios";
import Swal from "sweetalert2";

// Import your utils & constants
import colors from "../../constants/colors";
import vehicle_types from "../../constants/vehicle_types";
import { formatDate, formatDateTime } from "../../utils/date_format";
import { stringToUpper, removeSpaces } from "../../utils/data_transform";
import { mask_number } from "../../utils/mask_number";
import { sha256 } from "../../constants/hashing";
import {
  base, reporter_by_id, states as stateUrl, lgs as lgsUrl, confirm_token,
  manufacturers as manufacturesUrl, models as modelsUrl, report as reportUrl,
  upload as uploadUrl, verify, send_token, reporter, reporter_login, get_formations
} from "../../constants/urls";

// Import Step Components
import LoginStep from "./steps/LoginStep";
import CreateProfileStep from "./steps/CreateProfileStep";
import ConfirmContactStep from "./steps/ConfirmContactStep";
import TokenStep from "./steps/TokenStep";
import ReporterDetailsStep from "./steps/ReporterDetailsStep";
import OwnerVerificationStep from "./steps/OwnerVerificationStep";
import VehicleSpecsStep from "./steps/VehicleSpecsStep";
import TheftIncidentStep from "./steps/TheftIncidentStep";
import DocumentUploadStep from "./steps/DocumentUploadStep";
import RepresentativeDetailsStep from "./steps/RepresentativeDetailsStep";

export default function ReportingForm() {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [step, setStep] = useState(1);
  const [states, setStates] = useState([]);
  const [lgs, setLocalGovernments] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [_reporter_id, setReporterId] = useState(0);
  const [id_type, setIdType] = useState("");
  const [token_channel, setChannel] = useState("");
  const [token_destination, setDestination] = useState("");
  const [police_formations, setPoliceFormations] = useState([]);
  const [ownerDetails, setOwnerDetails] = useState({});
  const [reporterIdNumber, setreporterIdNumber] = useState("");
  const [profileType, setProfileType] = useState("individual");
  const [selectedColor, setSelectedColor] = useState(colors[0]);
  const [query, setQuery] = useState("");
  const [stolen_time, setTimeOfStealing] = useState(new Date());
  const [date_reported, setDateReported] = useState(new Date());
  const [reportId, setReportId] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifyingNewOwner, setIsVerifyingNewOwner] = useState(false);
  const [owner, setOwner] = useState("");
  const [stolen_state, setStolenState] = useState("");
  const [owner_details, populateOwnerDetails] = useState(false);

  const { register, formState: { errors }, handleSubmit } = useForm();
  
  const [inputs, setInputs] = useState({
    state: "", lga: "", first_name: "", middle_name: "", last_name: "", gender: "", email: "", phone: "",
    registered_address: "", present_address: "", r_profile_type: "", r_id_number: "", r_id_type: "",
    token: "", r_first_name: "", r_middle_name: "", r_last_name: "", r_gender: "", r_email: "",
    r_phone: "", r_address: "", r_state: "", r_lga: "", r_otp_method: "", pulled_phone: "",
    pulled_email: "", owner: "", owner_id_type: "", owner_id_number: "", owner_id: "", reporter_id: "",
    manufacturer: "", model: "", state_stolen: "", lg_stolen: "", stolen_time: "", current_number: "",
    r_confirm_number: "", relationship_to_owner: "", valid_email: "",
  });

  // ==========================================
  // 2. LIFECYCLE EFFECTS
  // ==========================================
  useEffect(() => {
    let url = base + stateUrl;
    if (!validator.isURL(url)) return;
    fetch(url).then((res) => res.json()).then((resJson) => setStates(resJson.data));
  }, []);

  useEffect(() => {
    let url = base + manufacturesUrl+"/motorvehicle";
    if (!validator.isURL(url)) return;
    fetch(url).then((res) => res.json()).then((resJson) => setManufacturers(resJson.data));
  }, []);

  useEffect(() => {
    var max = new Date().getFullYear();
    var min = 1980;
    var yearsArr = [];
    for (var i = max; i >= min; i--) { yearsArr.push(i); }
    setYears(yearsArr);
  }, []);

  // ==========================================
  // 3. LOGIC & HANDLERS
  // ==========================================
  const nextStep = () => {
    if ((inputs.r_address === "" || inputs.r_address === undefined) && step === 5) {
      Swal.fire({ title: "Error!", text: "Address is required", icon: "error", confirmButtonColor: "#1C89DE" });
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const getLgs = async (state) => {
    const url = base + lgsUrl + state;
    fetch(url).then((res) => res.json()).then((resJson) => {
      if (resJson.statusCode === 0) setLocalGovernments(resJson.data);
    });
  };

  const getModels = async (manufacturer) => {
    const url = base + modelsUrl + manufacturer+"/motorvehicle";
    fetch(url).then((res) => res.json()).then((resJson) => {
      if (resJson.statusCode === 0) setModels(resJson.data);
    });
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
    
    if (e.target.name === "r_state" || e.target.name === "state_stolen" || e.target.name === "state") {
      getLgs(e.target.value);
    } else if (e.target.name === "manufacturer") {
      getModels(e.target.value);
    } else if (e.target.name === "owner") {
      setOwner(e.target.value);
      if (e.target.value === "no") {
        setOwnerDetails({ first_name: "", middle_name: "", last_name: "", gender: "", email: "", phone: "", state: "", lga: "", registered_address: "", id_type: "", id_number: "", o_profile_type: profileType });
      } else {
        setInputs({ ...inputs, first_name: inputs.r_first_name, middle_name: inputs.r_middle_name, last_name: inputs.r_last_name, gender: inputs.r_gender, email: inputs.r_email, phone: inputs.r_phone, state: inputs.r_state, lga: inputs.r_lga, registered_address: inputs.r_address, id_type: inputs.r_id_type, id_number: inputs.r_id_number, o_profile_type: profileType, owner_id: _reporter_id });
        setOwnerDetails({ first_name: inputs.r_first_name, middle_name: inputs.r_middle_name, last_name: inputs.r_last_name, gender: inputs.r_gender, email: inputs.r_email, phone:inputs.r_phone, state:inputs.r_state, lga: inputs.r_lga, registered_address: inputs.r_address, id_type: inputs.r_id_type, id_number: inputs.r_id_number, o_profile_type: profileType });
        nextStep();
      }
    }
  };

  const handleStolenStateChange = (e) => {
    getLgs(e.target.value);
    const url = base + get_formations + e.target.value;
    fetch(url).then((res) => res.json()).then((resJson) => {
      if (resJson.statusCode === 0) setPoliceFormations(resJson.data);
    });
  };

  const sendToken = (channel, destination, auxiliary) => {
    let tokenUrl = base + send_token + channel + "/" + destination + "/" + auxiliary;
    if (!validator.isURL(tokenUrl)) return;
    axios({ method: "get", url: tokenUrl }).then((response) => {
      if (response.status === 200) return true;
      return false;
    });
  };

  const onConfirmPhoneNumber = () => {
    setChannel("sms");
    setDestination(inputs.r_phone);
    sendToken("sms", inputs.r_phone, inputs.r_email);
    setStep(4);
  };

  const onLogin = (useForm_data) => {
    let url = base + reporter_login + useForm_data.login_email;
    if (!validator.isURL(url)) return;
    axios({ method: "get", url: url }).then((response) => {
      if (response.data.statusCode === 0) {
        setDestination(useForm_data.login_email);
        setChannel("email");
        setReporterId(response.data.data.id);
        let responseData = response.data.data;
        sendToken("email", useForm_data.login_email, useForm_data.phone);
        
        setreporterIdNumber(responseData.id_number);
        setInputs({
          ...inputs, r_first_name: responseData.first_name, r_middle_name: responseData.middle_name, r_last_name: responseData.last_name, r_phone: responseData.phone_number, r_email: responseData.email, r_address: responseData.address, r_gender: responseData.gender, r_state: responseData.state, r_id_type: responseData.id_type, r_id_number: responseData.id_number, r_lga: responseData.lg
        });
        
        if (!responseData.last_name && !responseData.middle_name) {
          setProfileType("corporate");
        } else {
          setProfileType("individual");
        }
        setIsVerified(true);
        setStep(4);
      } else {
        Swal.fire({ title: "Error!", text: useForm_data.login_email + " not found. Create new profile.", icon: "error", confirmButtonColor: "#1C89DE" });
      }
    });
  };

  const onVerify = (e) => {
    e.preventDefault();
    setIsVerifying(true);
    setreporterIdNumber(inputs.r_id_number);
    setIdType(inputs.r_id_type);
    
    if (!inputs.r_id_type) { setIsVerifying(false); Swal.fire({ title: "Error!", text: "ID type required", icon: "error" }); return; }
    if (!inputs.r_id_number) { setIsVerifying(false); Swal.fire({ title: "Error!", text: "ID number required", icon: "error" }); return; }
    if (!inputs.r_profile_type) { setIsVerifying(false); Swal.fire({ title: "Error!", text: "Profile type required", icon: "error" }); return; }

    if (inputs.r_id_type === "nin") { setStep(5); } 
    else if (inputs.r_profile_type === "corporate") { setStep(10); } 
    else if (inputs.r_id_type === "dl") { setStep(4); } 
    else { setStep(1); }
    setIsVerifying(false);
  };

  const onConfirmToken = (e) => {
    e.preventDefault();
    setIsVerifying(true);
    let confirmTokenUrl = base + confirm_token + token_destination + "/" + inputs.token;
    if (!validator.isURL(confirmTokenUrl)) return;
    axios({ method: "get", url: confirmTokenUrl }).then((response) => {
      if (response.status === 200 && response.data.statusCode === 0) {
        setIsVerifying(false);
        Swal.fire({ title: "Success!", text: "Token Confirmed", icon: "success", confirmButtonColor: "#1C89DE" }).then(() => {
          if (token_channel === "sms" || token_channel === "voice") { nextStep(); } else { setStep(6); }
        });
      } else {
        setIsVerifying(false);
        Swal.fire({ title: "Error!", text: "Token Confirmation failed", icon: "error", confirmButtonColor: "#1C89DE" });
      }
    });
  };

  const verifyCorporateReporter = (e) => {
    e.preventDefault();
    setIsVerifyingNewOwner(true);
    let url = base + verify;
    if (!validator.isURL(url)) return;
    let data = { idType: inputs.r_id_type, id: inputs.r_id_number };
    axios({ method: "post", url: url, data }).then((response) => {
      let resp = response.data.data;
      if (response.data.statusCode === 6) {
        setInputs({ ...inputs, r_profile_type: "individual", r_first_name: resp.first_name, r_middle_name: resp.middle_name, r_last_name: resp.last_name, r_gender: resp.gender, r_email: resp.email, r_phone: resp.phone_number, r_address: resp.address, r_state: resp.state, r_lga: resp.lg });
      } else if (inputs.r_id_type === "nin") {
        setInputs({ ...inputs, r_profile_type: "individual", r_first_name: resp.firstname, r_middle_name: resp.middlename, r_last_name: resp.surname, r_gender: resp.gender === "f" || resp.gender === "F" ? "Female" : "Male", r_email: resp.email, r_phone: resp.telephoneno, r_address: resp.residence_AdressLine1 + " " + resp.residence_Town, r_state: resp.residence_state, r_lga: resp.residence_lga });
      } else if (inputs.r_id_type === "dl") {
        setInputs({ ...inputs, r_profile_type: "individual", r_first_name: resp.first_name, r_middle_name: resp.middle_name, r_last_name: resp.last_name, r_gender: resp.gender === "M" ? "Male" : "Female", r_email: resp.email, r_phone: resp.phone_number, r_address: resp.address, r_state: "", r_lga: "" });
      }
      setIsVerifyingNewOwner(false);
    });
  };

  const onSubmitReporter = (useForm_data) => {
    setIsLoading(true);
    let data = {
      first_name: inputs.r_first_name?.toUpperCase(), middle_name: inputs.r_middle_name?.toUpperCase(), last_name: inputs.r_last_name?.toUpperCase(), gender: inputs.r_gender, email: step === 10 ? useForm_data.email : inputs.r_email, phone_number: useForm_data.r_phone, id_type: id_type, pulled_email: inputs.pulled_email, pulled_phone: inputs.pulled_phone, id_number: useForm_data.r_id_number, state: useForm_data.r_state, lg: useForm_data.r_lga, address: inputs.r_address, status: "Pending"
    };
    let url = base + reporter;
    if (!validator.isURL(url)) return;
    axios({ method: "post", url: url, data }).then((response) => {
      if (response.data.statusCode === 0) {
        setReporterId(response.data.data);
        if (step === 10) { setStep(7); } else { nextStep(); }
        setIsLoading(false);
      }
    });
  };

  const onSubmit = (useForm_data) => {
    setIsLoading(true);
    inputs.stolen_time = formatDateTime(stolen_time);
    inputs.color = selectedColor;

    let data = {
      reporter_id: parseInt(_reporter_id),
      owner: {
        first_name: ownerDetails.first_name, middle_name: ownerDetails.middle_name, last_name: ownerDetails.last_name, type: inputs.o_profile_type === undefined ? profileType : inputs.o_profile_type, gender: owner === "yes" ? ownerDetails.gender : useForm_data.gender, email: owner === "yes" ? ownerDetails.email : useForm_data.email, phone: ownerDetails.id_type === "nin" ? ownerDetails.phone : useForm_data.phone, state: ownerDetails.id_type === "nin" ? ownerDetails.state : useForm_data.state, lg: owner === "yes" ? ownerDetails.lga : useForm_data.lga, registered_address: ownerDetails.registered_address, present_address: useForm_data.present_address, id_type: ownerDetails.id_type, id_number: ownerDetails.id_number, status: "Not Verified",
      },
      vehicle: {
        license_plate: stringToUpper(removeSpaces(useForm_data.license_plate)), chassis_number: stringToUpper(removeSpaces(useForm_data.chassis_number)), engine_number: stringToUpper(useForm_data.engine_number), manufacturer: useForm_data.manufacturer, model: useForm_data.model, year: useForm_data.year, vehicle_type: useForm_data.vehicle_type, color: useForm_data.color, vehicle_usage: useForm_data.vehicle_usage, vehicle_status: "Reported", mla: useForm_data.mla, mla_address: useForm_data.mla_address, vehicle_source: useForm_data.source_of_vehicle,
      },
      report: {
        reporter_id: parseInt(_reporter_id), entry_date: formatDate(new Date()), where_state: useForm_data.state_stolen, where_lg: useForm_data.lg_stolen, where_location: useForm_data.stolen_location, time_of_stealing: formatDateTime(stolen_time), mode_of_stealing: useForm_data.mode_of_stealing, where_reported: useForm_data.police_formation, date_reported: formatDate(date_reported), description: useForm_data.detailed_description, extract_number: useForm_data.extract_number, reported_by: owner === "yes" ? "Owner" : useForm_data.relationship, status: "Pending",
      },
    };
    let url = base + reportUrl;
    if (!validator.isURL(url)) return;
    axios({ method: "post", url: url, data }).then((response) => {
      if (response.data.statusCode === 0) {
        setReportId(response.data.data.reportId);
        nextStep();
        setIsLoading(false);
      }
    });
  };

  const onUpload = async (inputs) => {
    setIsLoading(true);
    if (reportId === 0) { setIsLoading(false); return; }
    
    const extract = document.getElementById("police_extract");
    const proof = document.getElementById("proof_of_ownership");
    const affidavit = document.getElementById("affidavit");
    
    const url = base + uploadUrl;
    if (!validator.isURL(url)) return;
    
    const formData = new FormData();
    formData.append("id", reportId);
    formData.append("actor_type", "reporter");
    formData.append("actor_id", _reporter_id);
    
    if (extract.files.length) formData.append("police_extract", extract.files[0]);
    if (proof.files.length) formData.append("proof", proof.files[0]);
    if (affidavit.files.length) formData.append("affidavit", affidavit.files[0]);
    
    try {
      const response = await axios.post(url, formData, { headers: { "Content-Type": "multipart/form-data" }});
      setIsLoading(false);
      if (response.data.statusCode === 0) {
        const results = response.data.data;
        const failedFiles = results.filter(r => r.status === "error");
        if (failedFiles.length > 0) {
          Swal.fire({ title: "Partial Upload", html: failedFiles.map(f => `<p>${f.field}: ${f.error}</p>`).join(""), icon: "warning", confirmButtonColor: "#1C89DE" });
        } else {
          Swal.fire({ title: "Success!", text: "All files uploaded successfully", icon: "success", confirmButtonColor: "#1C89DE" }).then(() => { window.location = "/"; });
        }
      } else {
        Swal.fire({ title: "Error!", text: "Error uploading files", icon: "error", confirmButtonColor: "#1C89DE" });
      }
    } catch (err) {
      setIsLoading(false);
      Swal.fire({ title: "Error!", text: "An unexpected error occurred", icon: "error", confirmButtonColor: "#1C89DE" });
    }
  };

  // ==========================================
  // 4. PROP BUNDLING FOR STEPS
  // ==========================================
  const stepProps = {
    step, setStep, nextStep, prevStep, register, errors, inputs, setInputs, handleChange, handleSubmit,
    isLoading, isVerifying, isVerified, isVerifyingNewOwner, states, lgs, manufacturers, models, years, 
    police_formations, ownerDetails, setOwnerDetails, profileType, setProfileType, owner, setOwner,
    stolen_time, setTimeOfStealing, date_reported, setDateReported, token_destination, handleStolenStateChange,
    onLogin, onVerify, onConfirmPhoneNumber, onConfirmToken, onSubmitReporter, onSubmit, onUpload,
    verifyCorporateReporter, sendToken, mask_number
  };

  // ==========================================
  // 5. RENDER ENGINE
  // ==========================================
  const renderStep = () => {
    switch (step) {
      case 0: return <LoginStep {...stepProps} />;
      case 1: return <CreateProfileStep {...stepProps} />;
      case 2: return <ConfirmContactStep {...stepProps} />;
      case 4: return <TokenStep {...stepProps} />;
      case 5: return <ReporterDetailsStep {...stepProps} />;
      case 6: return <OwnerVerificationStep {...stepProps} />;
      case 7: return <VehicleSpecsStep {...stepProps} />;
      case 8: return <TheftIncidentStep {...stepProps} />;
      case 9: return <DocumentUploadStep {...stepProps} />;
      case 10: return <RepresentativeDetailsStep {...stepProps} />;
      default: return null;
    }
  };

  return (
    <div className="w-full">
      <form encType="multipart/form-data" className="space-y-8">
        {renderStep()}
      </form>
    </div>
  );
}